import { useState, useEffect, useMemo } from 'react';
import { Box, Button, IconButton, Tooltip, MenuItem, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Menu, CircularProgress, Checkbox } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Plus, Edit, X, ChevronDown, Upload, CalendarX } from 'lucide-react';
import { FcBusinessman, FcBusinesswoman } from 'react-icons/fc';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useStudents } from '@/hooks/useStudents';
import { useAuth } from '@/hooks/useAuth';
import { ROLES } from '@/constants/roles';
import { tokens } from '@/theme/tokens';
import useStudentStore from '@/store/studentStore';
import toast from 'react-hot-toast';
import { useSchool } from '@/hooks/useSchool';
import { useDebounce } from '@/hooks/useDebounce';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import { enumApi } from '@/api/enum.api';
import { StudentListFilters } from './components/StudentListFilters';
import { BulkImportStudentModal } from './components/BulkImportStudentModal';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, borderBottom: `1px solid ${tokens.colors.border}`, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

const DEFAULT_STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Transferred', value: 'TRANSFERRED' },
  { label: 'Passed Out', value: 'PASSED_OUT' },
  { label: 'Dropped Out', value: 'DROPPED_OUT' },
  { label: 'Deleted', value: 'DELETED' },
];

export function StudentListPage() {
  usePageMeta('Student Directory');

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const { deleteStudent, bulkUpdateStudentStatus } = useStudentStore();

  const [classesData, setClassesData] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [hasActiveSession, setHasActiveSession] = useState(true);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState({ open: false, student: null, loading: false });
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Bulk Selection & Status State
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [singleStudentTarget, setSingleStudentTarget] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);

  useEffect(() => {
    let isMounted = true;
    enumApi.getStudentStatuses()
      .then((res) => {
        if (isMounted && Array.isArray(res) && res.length > 0) {
          setStatusOptions(res);
        }
      })
      .catch((err) => {
        console.warn('Failed to load status enums:', err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const [advancedFilters, setAdvancedFilters] = useState({
    search: '',
    academic_session_ids: [],
    academic_session_id: '',
    class_ids: [],
    class_id: '',
    section_ids: [],
    section_id: '',
    student_statuses: location.state?.status ? [location.state.status.toUpperCase()] : ['ACTIVE'],
    student_status: location.state?.status?.toUpperCase() || 'ACTIVE',
    enrollment_statuses: location.state?.enrollmentStatus ? [location.state.enrollmentStatus.toUpperCase()] : ['ACTIVE'],
    enrollment_status: location.state?.enrollmentStatus?.toUpperCase() || 'ACTIVE',
    genders: location.state?.gender ? [location.state.gender.toUpperCase()] : [],
    gender: location.state?.gender?.toUpperCase() || '',
    blood_groups: [],
    blood_group: '',
    religions: [],
    religion: '',
    categories: [],
    category: '',
    castes: [],
    caste: '',
    nationalities: [],
    nationality: '',
    admission_types: location.state?.admissionType ? [location.state.admissionType.toUpperCase()] : [],
    admission_type: location.state?.admissionType?.toUpperCase() || '',
    is_rte: '',
    roll_number: '',
    father_name: '',
    father_mobile: '',
    father_email: '',
    father_occupation: '',
    mother_name: '',
    mother_mobile: '',
    mother_email: '',
    mother_occupation: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_mobile: '',
    has_mobile: '',
    has_email: '',
    has_aadhaar: '',
    annual_income_min: '',
    annual_income_max: '',
    admission_no: '',
    admission_date_from: '',
    admission_date_to: '',
    date_of_birth_from: '',
    date_of_birth_to: '',
    sort_by: '',
    sort_order: 'asc',
  });

  const [sortModel, setSortModel] = useState([]);

  const debouncedFilters = useDebounce(advancedFilters, 400);

  const finalFilters = useMemo(() => {
    const f = { ...debouncedFilters };
    if (sortModel.length > 0) {
      const fieldMap = {
        student_name: 'first_name',
      };
      f.sort_by = fieldMap[sortModel[0].field] || sortModel[0].field;
      f.sort_order = sortModel[0].sort;
    }
    return f;
  }, [debouncedFilters, sortModel]);

  useEffect(() => {
    if (!schoolId) return;

    async function fetchInitialData() {
      try {
        const sessionsRes = await academicSessionsApi.getAllBySchool(schoolId);
        const fetchedSessions = Array.isArray(sessionsRes) ? sessionsRes : [];
        setSessions(fetchedSessions);

        if (fetchedSessions.length === 0) {
          setHasActiveSession(false);
        } else {
          const current = fetchedSessions.find((s) => s.is_current) || fetchedSessions[0];
          if (current) {
            setAdvancedFilters(prev => ({
              ...prev,
              academic_session_ids: [current.id],
              academic_session_id: current.id
            }));
            setHasActiveSession(true);
          } else {
            setHasActiveSession(false);
          }
        }
      } catch {
        setHasActiveSession(false);
      } finally {
        setSessionsLoaded(true);
      }
    }
    fetchInitialData();
  }, [schoolId]);

  useEffect(() => {
    if (schoolId) {
      studentsApi.getClassesSections().then(res => {
        setClassesData(res?.data || []);
      }).catch(console.error);
    }
  }, [schoolId]);

  const isFilterReady = sessionsLoaded && (!hasActiveSession || Boolean(advancedFilters.academic_session_id || (Array.isArray(advancedFilters.academic_session_ids) && advancedFilters.academic_session_ids.length > 0)));

  const { students, pagination, loading, error, refetch, page, setPage, pageSize, setPageSize } = useStudents(1, 20, finalFilters, { enabled: isFilterReady });

  useEffect(() => {
    if (error && hasActiveSession) {
      toast.error(error, { id: typeof error === 'string' ? error : 'student-list-error' });
    }
  }, [error, hasActiveSession]);

  const roleUpper = String(user?.role || '').toUpperCase();
  const canManage =
    !roleUpper ||
    roleUpper === 'PRINCIPAL' ||
    roleUpper === 'ORGANIZATION_ADMIN' ||
    roleUpper === 'RECEPTION' ||
    roleUpper !== 'TEACHER';

  const handleApplyStatusChange = async (newStatus) => {
    setStatusMenuAnchor(null);
    if (!schoolId) return;

    const targetIds = singleStudentTarget
      ? [singleStudentTarget.student_id || singleStudentTarget.id]
      : rowSelectionModel;

    if (!targetIds || targetIds.length === 0) return;

    setBulkUpdating(true);
    try {
      const res = await bulkUpdateStudentStatus(schoolId, targetIds, newStatus);
      toast.success(res?.data?.message || res?.message || `Status updated for ${targetIds.length} student(s)`);
      setRowSelectionModel([]);
      setSingleStudentTarget(null);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update student status');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleOpenDeactivate = (student) => {
    setDeactivateDialog({ open: true, student, loading: false });
  };

  const handleConfirmDeactivate = async () => {
    const studentId = deactivateDialog.student?.student_id || deactivateDialog.student?.id;
    if (!studentId) return;
    setDeactivateDialog((prev) => ({ ...prev, loading: true }));
    try {
      await deleteStudent(schoolId, studentId);
      toast.success('Student deactivated successfully');
      setDeactivateDialog({ open: false, student: null, loading: false });
      refetch();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate student');
      setDeactivateDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 38,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const pageStudentIds = (Array.isArray(students) ? students : []).map((s) => s.student_id || s.id).filter(Boolean);
        const allSelected = pageStudentIds.length > 0 && pageStudentIds.every((id) => rowSelectionModel.includes(id));
        const someSelected = pageStudentIds.some((id) => rowSelectionModel.includes(id)) && !allSelected;
        return (
          <Checkbox
            size="small"
            indeterminate={someSelected}
            checked={allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setRowSelectionModel((prev) => Array.from(new Set([...prev, ...pageStudentIds])));
              } else {
                setRowSelectionModel((prev) => prev.filter((id) => !pageStudentIds.includes(id)));
              }
            }}
          />
        );
      },
      renderCell: (params) => {
        const id = params.row.student_id || params.row.id;
        const isChecked = rowSelectionModel.includes(id);
        return (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Checkbox
              size="small"
              checked={isChecked}
              onChange={(e) => {
                if (e.target.checked) {
                  setRowSelectionModel((prev) => [...prev, id]);
                } else {
                  setRowSelectionModel((prev) => prev.filter((item) => item !== id));
                }
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'student_name',
      headerName: 'Student Name',
      flex: 1.15,
      minWidth: 140,
      valueGetter: (params, row) => row?.student_name || '',
      renderCell: (params) => {
        const isFemale = String(params.row?.gender || '').toUpperCase() === 'FEMALE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
            <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isFemale ? <FcBusinesswoman size={24} /> : <FcBusinessman size={20} />}
            </Box>
            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8125rem' }}>{params.row?.student_name || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'admission_no',
      headerName: 'Adm. No.',
      flex: 0.85,
      minWidth: 105,
      valueGetter: (val, row) => row?.admission_no || '-'
    },
    {
      field: 'sr_number',
      headerName: 'SR No.',
      flex: 0.5,
      minWidth: 60,
      valueGetter: (val, row) => row?.sr_number || row?.sr_no || '-'
    },
    {
      field: 'roll_number',
      headerName: 'Roll No.',
      flex: 0.5,
      minWidth: 60,
      valueGetter: (val, row) => row?.roll_number || '-'
    },
    {
      field: 'class_name',
      headerName: 'Class',
      flex: 0.6,
      minWidth: 70,
      valueGetter: (val, row) => row?.class?.name || row?.class_name || row?.className || '-'
    },
    {
      field: 'section_name',
      headerName: 'Section',
      flex: 0.5,
      minWidth: 60,
      valueGetter: (val, row) => row?.section?.name || row?.section_name || row?.sectionName || '-'
    },
    {
      field: 'gender',
      headerName: 'Gender',
      flex: 0.6,
      minWidth: 70,
      valueGetter: (val, row) => row?.gender || '-'
    },
    {
      field: 'father_name',
      headerName: 'Father Name',
      flex: 1.0,
      minWidth: 125,
      valueGetter: (val, row) => row?.parent?.father_name || row?.father_name || '-',
      renderCell: (params) => params.row?.parent?.father_name || params.row?.father_name || '-'
    },
    {
      field: 'mobile',
      headerName: 'Phone',
      flex: 0.8,
      minWidth: 100,
      valueGetter: (val, row) => row?.mobile || row?.parent?.father_mobile || row?.father_mobile || '-',
      renderCell: (params) => params.row?.mobile || params.row?.parent?.father_mobile || params.row?.father_mobile || '-'
    },
    {
      field: 'student_status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 90,
      valueGetter: (val, row) => row?.student_status || row?.status || 'ACTIVE',
      renderCell: (params) => {
        const rawStatus = params.row?.student_status || params.row?.status || 'ACTIVE';
        const st = String(rawStatus).toUpperCase();
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title="Click to change status">
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setSingleStudentTarget(params.row);
                  setStatusMenuAnchor(e.currentTarget);
                }}
                sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              >
                <StatusBadge
                  status={st === 'ACTIVE' ? 'success' : st === 'INACTIVE' || st === 'DELETED' ? 'error' : st === 'TRANSFERRED' ? 'warning' : 'info'}
                  label={st}
                />
              </Box>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'enrollment_status',
      headerName: 'Enrollment',
      flex: 0.75,
      minWidth: 95,
      valueGetter: (val, row) => row?.enrollment_status || 'ACTIVE',
      renderCell: (params) => {
        const rawStatus = params.row?.enrollment_status || 'ACTIVE';
        const st = String(rawStatus).toUpperCase();
        const badgeType = st === 'ACTIVE' ? 'success' : st === 'COMPLETED' ? 'info' : (st === 'DROPPED_OUT' || st === 'SUSPENDED' || st === 'CANCELLED') ? 'error' : 'warning';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <StatusBadge
              status={badgeType}
              label={st}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 75,
      minWidth: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const studentId = params.row.student_id || params.row.id;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit size={13} />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/students/edit/${studentId}`);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: '6px',
                py: 0.25,
                px: 1,
                minWidth: 'auto',
                color: tokens.colors.primary.main,
                borderColor: `${tokens.colors.primary.main}40`,
                backgroundColor: '#ffffff',
                '&:hover': {
                  borderColor: tokens.colors.primary.main,
                  backgroundColor: `${tokens.colors.primary.main}12`,
                },
              }}
            >
              Edit
            </Button>
          </Box>
        );
      },
    },
  ];

  if (sessionsLoaded && !hasActiveSession) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Student Directory"
          subtitle="Manage and view all students in the school."
        />
        <WidgetContainer>
          <Box
            sx={{
              py: 6,
              px: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: tokens.colors.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <CalendarX size={36} color={tokens.colors.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              No Active Academic Session Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 3.5, lineHeight: 1.6 }}>
              There is no active academic session configured for this school. Please create or activate an academic session first to view or add students.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Plus size={20} />}
              onClick={() => navigate('/academic-sessions')}
              sx={{ borderRadius: tokens.radius.full, px: 4, py: 1.2 }}
            >
              Create Academic Session
            </Button>
          </Box>
        </WidgetContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title="Student Directory"
        subtitle="Manage and view all students in the school."
        action={
          canManage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Upload size={18} />}
                onClick={() => setImportModalOpen(true)}
                sx={{ borderRadius: tokens.radius.md, fontWeight: 600 }}
              >
                Bulk Import
              </Button>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => navigate('/students/add')}
                sx={{ borderRadius: tokens.radius.md, fontWeight: 600 }}
              >
                Add Student
              </Button>
            </Box>
          )
        }
      />

      <WidgetContainer noPadding>
        <StudentListFilters
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          sessions={sessions}
          classesData={classesData}
          onClear={() => {
            setAdvancedFilters({
              search: '',
              academic_session_ids: sessions.length > 0 ? [(sessions.find(s => s.is_current) || sessions[0]).id] : [],
              academic_session_id: sessions.length > 0 ? (sessions.find(s => s.is_current) || sessions[0]).id : '',
              class_ids: [], class_id: '', section_ids: [], section_id: '',
              student_statuses: ['ACTIVE'], student_status: 'ACTIVE',
              enrollment_statuses: ['ACTIVE'], enrollment_status: 'ACTIVE',
              genders: [], gender: '', blood_groups: [], blood_group: '',
              religions: [], religion: '', categories: [], category: '',
              castes: [], caste: '', nationalities: [], nationality: '',
              admission_types: [], admission_type: '', is_rte: '', roll_number: '',
              father_name: '', father_mobile: '', father_email: '', father_occupation: '',
              mother_name: '', mother_mobile: '', mother_email: '', mother_occupation: '',
              guardian_name: '', guardian_relation: '', guardian_mobile: '',
              has_mobile: '', has_email: '', has_aadhaar: '',
              annual_income_min: '', annual_income_max: '', admission_no: '',
              admission_date_from: '', admission_date_to: '', date_of_birth_from: '', date_of_birth_to: '',
              sort_by: '', sort_order: 'asc',
            });
            setSortModel([]);
            setRowSelectionModel([]);
          }}
          onApply={() => refetch()}
        />

        {/* Bulk Action Bar */}
        {rowSelectionModel.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              px: 2.5,
              m: 2,
              mb: 0,
              bgcolor: `${tokens.colors.primary.main}10`,
              border: `1px solid ${tokens.colors.primary.main}30`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Typography variant="body2" fontWeight={600} color="primary.main">
              {rowSelectionModel.length} student{rowSelectionModel.length > 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={bulkUpdating}
                onClick={(e) => {
                  setSingleStudentTarget(null);
                  setStatusMenuAnchor(e.currentTarget);
                }}
                startIcon={bulkUpdating ? <CircularProgress size={16} color="inherit" /> : <ChevronDown size={16} />}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
              >
                {bulkUpdating ? 'Updating...' : 'Change Status'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => setRowSelectionModel([])}
                sx={{ borderRadius: 1.5, textTransform: 'none' }}
              >
                Clear Selection
              </Button>
            </Box>
          </Paper>
        )}

        {error ? (
          <Box sx={{ p: 4 }}>
            <ErrorState
              title="Failed to Load Students"
              message={error}
              onRetry={refetch}
            />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={Array.isArray(students) ? students : []}
              columns={columns}
              loading={loading || bulkUpdating}
              getRowId={(row) => row?.student_id || row?.id || String(Math.random())}
              paginationMode="server"
              rowCount={pagination.total}
              pageSizeOptions={[10, 20, 50, 100]}
              paginationModel={{
                page: page - 1, // DataGrid is 0-indexed
                pageSize: pageSize
              }}
              onPaginationModelChange={(model) => {
                setPage(model.page + 1);
                setPageSize(model.pageSize);
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(newModel) => setSortModel(newModel)}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/students/${params.row.student_id || params.row.id}`)}
              slots={{ toolbar: CustomToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
                '& .MuiDataGrid-cell': { px: 1 },
                '& .MuiDataGrid-columnHeader': { px: 1 },
              }}
            />
          </Box>
        )}
      </WidgetContainer>

      {/* Status Selection Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => {
          setStatusMenuAnchor(null);
          setSingleStudentTarget(null);
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { borderRadius: 2, minWidth: 160 }
          }
        }}
      >
        {statusOptions.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleApplyStatusChange(opt.value)}
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            {opt.label || opt.value}
          </MenuItem>
        ))}
      </Menu>

      {/* Compact Deactivate Dialog */}
      <Dialog
        open={deactivateDialog.open}
        onClose={() => setDeactivateDialog({ open: false, student: null, loading: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pt: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Deactivate Student
          </Typography>
          <IconButton size="small" onClick={() => setDeactivateDialog({ open: false, student: null, loading: false })}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 2 }}>
          {deactivateDialog.student && (
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to deactivate <strong>{deactivateDialog.student.student_name}</strong> ({deactivateDialog.student.admission_no})?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setDeactivateDialog({ open: false, student: null, loading: false })}
            disabled={deactivateDialog.loading}
            sx={{ borderRadius: 1.5, px: 2, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleConfirmDeactivate}
            disabled={deactivateDialog.loading}
            sx={{ borderRadius: 1.5, fontWeight: 600, px: 2.5, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {deactivateDialog.loading ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Students Modal */}
      <BulkImportStudentModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => refetch()}
      />
    </Box>
  );
}
